import { useEffect, useMemo, useState } from 'react'
import { Box, Text } from '@grupoboticario/flora-react'
import { useTranslation } from 'react-i18next'
import ReactSignatureCanvas from 'react-signature-canvas'
import { ExclamationMarkHexagonFillIcon } from '@grupoboticario/flora-react-icons'

import useSignatureSize from '../hooks/useSignatureSize'

export type TProps = {
  id: string
  label: string
  hasError: boolean
  signature: string
  disabled?: boolean
  onChange: (img: string) => void
}

const Signature = ({ id, label, hasError, signature, disabled = false, onChange }: TProps) => {
  const { t } = useTranslation()
  const [canvasRef, setCanvasRef] = useState<ReactSignatureCanvas | null>(null)
  const size = useSignatureSize(1100, 96)

  const handleSign = () => {
    if (!canvasRef) {
      return
    }

    if (disabled) {
      canvasRef.clear()
      return
    }

    onChange(canvasRef.toDataURL('image/x-png'))
  }

  useEffect(() => {
    if (!signature) canvasRef?.clear()
  }, [signature])

  const signatureShadow = useMemo(() => {
    return hasError
      ? { boxShadow: 'inset 0px 0px 0px 2px var(--flora--colors-error-standard)' }
      : { boxShadow: 'inset 0 0 0 1px var(--flora--colors-nonInteractiveAuxiliar)' }
  }, [hasError])

  return (
    <Box css={{ margin: '0px auto', width: size.width }}>
      <ReactSignatureCanvas
        canvasProps={{
          width: size.width,
          height: size.height,
          title: 'react-signature-canvas',
          id,
          style: {
            ...signatureShadow,
            borderRadius: '4px',
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.6 : 1
          }
        }}
        ref={(ref) => setCanvasRef(ref)}
        onEnd={handleSign}
      />

      {disabled && (
        <Text css={{ display: 'block', marginTop: '5px', fontSize: '12px', lineHeight: '16px' }}>
          {t('signatureLocked')}
        </Text>
      )}

      {hasError && (
        <Text
          color="$nonInteractivePredominant"
          css={{
            display: 'block',
            marginTop: '5px',
            fontSize: '12px',
            height: '16px',
            lineHeight: '16px',
            verticalAlign: 'middle'
          }}
        >
          <ExclamationMarkHexagonFillIcon color="$error-standard" size={14} css={{ marginRight: '5px' }} />
          {t('requiredField', { field: label })}
        </Text>
      )}
    </Box>
  )
}

export default Signature
